import type { TFunction } from "i18next";

/**
 * O vocabulário de papéis do produto, em um lugar só.
 *
 * Espelha `services/api/app/auth/roles.py` — quatro valores, e **só** eles. O
 * enunciado do SEC.8 falava em cinco, incluindo `key_user`: desatualizado. Desde
 * a migração `0085` o CHECK do banco recusa qualquer outro, e `key_user` sobrevive
 * no repositório apenas como valor **rejeitado** em `test_role_check_constraint.py`.
 *
 * A ordem do array é a ordem de alcance, do menor para o maior — `ROLE_LEVELS` é
 * derivado dela, então reordenar aqui reordena a hierarquia inteira.
 */
export const ROLES = ["user", "sales", "consultant", "manager", "admin"] as const;

export type Role = (typeof ROLES)[number];

/** Fail-closed, como o `role_level` do backend: fora do vocabulário não é papel. */
export function isRole(value: string | null | undefined): value is Role {
  return ROLES.includes(value as Role);
}

/**
 * O que um `manager` pode conceder ao criar conta por `POST /api/auth/register`.
 *
 * Espelha `roles.MANAGER_ASSIGNABLE_ROLES` — nem `admin`, que dá poder sobre os
 * outros, nem `manager`, que daria ao criado o poder de criar mais contas e
 * fecharia a fronteira em círculo. Os dois saem só por `POST /api/admin/users`,
 * que exige admin.
 *
 * ⚠️ **Não é a lista de papéis abaixo do chamador**, e por isso é uma constante
 * e não um cálculo: derivar de `roleLevel(caller) > roleLevel(alvo)` daria
 * `manager` como concedível, que é exatamente o que o backend recusa com 403.
 */
export const MANAGER_ASSIGNABLE_ROLES = ["consultant", "user"] as const satisfies readonly Role[];

export type ManagerAssignableRole = (typeof MANAGER_ASSIGNABLE_ROLES)[number];

/**
 * Guarda de tipo, e não um `as`: o `role` do formulário é um `Role` qualquer, e
 * o payload de `/register` só aceita dois. Estreitar de verdade faz o compilador
 * cobrar o caso que sobra, em vez de deixá-lo passar calado até virar 403.
 */
export function isManagerAssignable(role: string): role is ManagerAssignableRole {
  return (MANAGER_ASSIGNABLE_ROLES as readonly string[]).includes(role);
}

/**
 * Hierarquia de privilégio. Maior número = mais poder.
 *
 * Derivado da ordem de `ROLES` em vez de escrito à mão: duas listas do mesmo fato
 * divergem no primeiro ajuste, e a divergência aqui é silenciosa — nada quebra,
 * a tela só passa a mostrar a coisa errada para o papel errado.
 *
 * Os inteiros não têm significado próprio, só a ordem. Eles batem com os de
 * `roles.py` porque `roles.test.ts` lê o arquivo Python e compara.
 */
export const ROLE_LEVELS: Record<string, number> = Object.fromEntries(
  ROLES.map((role, i) => [role, i + 1]),
);

/**
 * Nível hierárquico de `role`.
 *
 * Fail-closed, exatamente como o `role_level` do backend: papel ausente, vazio,
 * com caixa trocada ou fora do vocabulário vale **0** e não satisfaz mínimo
 * nenhum — nem o de `user`. Um papel que sumiu do vocabulário perde acesso; não
 * ganha. É o comportamento certo enquanto o `/me` não resolve: sumir com a ação é
 * melhor que oferecer uma que pode falhar.
 */
export function roleLevel(role?: string | null): number {
  return ROLE_LEVELS[role ?? ""] ?? 0;
}

/**
 * `role` alcança pelo menos `min`?
 *
 * ⚠️ **Não é autorização.** Quem autoriza é o servidor, em toda requisição, contra
 * o banco — o papel que a tela conhece vem do `/me` e pode estar velho por até 15
 * minutos. Isto existe só para não oferecer um botão que sempre devolveria `403`,
 * e para não esconder o motivo atrás de um erro genérico.
 */
export function hasMinRole(role: string | null | undefined, min: Role): boolean {
  return roleLevel(role) >= roleLevel(min);
}

/**
 * O rótulo que a pessoa lê.
 *
 * Substitui o par `Owner`/`Editor` que a tela de Team usava: vocabulário morto,
 * que não cobria `manager` nem `consultant` (os dois caíam na string crua em
 * minúsculas) e que traduzia `Owner` de dois jeitos diferentes no mesmo bundle.
 *
 * Papel desconhecido devolve o valor cru — dado legado numa coluna aparece como
 * está, em vez de virar um rótulo bonito que mente sobre o que a conta alcança.
 */
export function roleLabel(role: string, t: TFunction): string {
  switch (role) {
    case "admin":
      return t("Administrator");
    case "manager":
      return t("Manager");
    case "consultant":
      return t("Consultant");
    case "sales":
      return t("Commercial");
    case "user":
      return t("User");
    default:
      return role;
  }
}

/** Uma frase curta sobre o alcance — o nome do papel sozinho não diz nada. */
export function roleBlurb(role: string, t: TFunction): string {
  switch (role) {
    case "admin":
      return t("reaches every project in the workspace");
    case "manager":
      return t("assigned to projects, approves scope");
    case "consultant":
      return t("migrates code and runs the agents");
    case "sales":
      return t("presents the Diagnostic and adjusts the BOM discount and quantity");
    case "user":
      return t("reads what they were given");
    default:
      return "";
  }
}

/** Tom do `Pill`, na paleta da marca. */
export function roleTone(role: string): "gold" | "navy" | "muted" {
  if (role === "admin") return "gold";
  if (role === "manager" || role === "consultant") return "navy";
  return "muted";
}

/**
 * Quem muda quantidade e desconto da BOM (#1714, cartão 6.3 do Marco).
 *
 * Espelha o `_BOM_ADJUSTERS` de `routers/bom.py`. É lista, e não nível: o
 * consultor e o manager estão acima do comercial na escada e ficam de fora.
 * Validar a linha sem mudar continua com quem alcança `sales`.
 */
export const BOM_ADJUSTERS = ["admin", "sales"] as const satisfies readonly Role[];

export function canAdjustBom(role: string | null | undefined): boolean {
  return (BOM_ADJUSTERS as readonly string[]).includes(role ?? "");
}

/**
 * Quem vê e edita a Calibragem comercial (EF-FUNC-012 v0.1 RF-01).
 *
 * Espelha o `COMMERCIAL_CALIBRATION_ROLES` de `routers/commercial_calibration.py`.
 * É lista, como a da BOM: o consultor e o manager estão acima do comercial na
 * escada e ficam de fora.
 */
export const COMMERCIAL_CALIBRATORS = ["admin", "sales"] as const satisfies readonly Role[];

